import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, LayoutDashboard, FilePlus, Link as LinkIcon, FileText, Settings, Moon, Sun, BellRing, PieChart, CheckCircle2, AlertCircle, X, BookOpen } from 'lucide-react';
import axios from 'axios';
import PUCForm from './components/PUCForm';
import NumberLinkForm from './components/NumberLinkForm';
import DailyBook from './components/DailyBook';
import Reports from './components/Reports';
import Renewals from './components/Renewals';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AccountBook from './components/AccountBook';
import './index.css';

const API_URL = `${process.env.REACT_APP_API_URL}/records`;
const NUMBER_LINKS_API_URL = `${process.env.REACT_APP_API_URL}/number-links`;

function App() {
  const [theme, setTheme] = useState('dark');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [records, setRecords] = useState([]);
  const [numberLinks, setNumberLinks] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [toast, setToast] = useState(null);
  const [toastError, setToastError] = useState(false);
  const [role, setRole] = useState(localStorage.getItem('role') || 'employee');

  // Setup Axios Interceptors for JWT
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          const isAuthFailure = error.response.status === 401 || 
            (error.response.status === 403 && error.response.data?.error === 'Invalid or expired token.');
          
          if (isAuthFailure) {
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            setToken(null);
            setRole('employee');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const fetchRecords = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(API_URL);
      setRecords(response.data);
      const nlResponse = await axios.get(NUMBER_LINKS_API_URL);
      setNumberLinks(nlResponse.data);
      if (role === 'admin') {
        const expResponse = await axios.get(`${process.env.REACT_APP_API_URL}/expenses`);
        setExpenses(expResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [token, role]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleAddRecord = async (newRecord) => {
    try {
      await axios.post(API_URL, newRecord);
      fetchRecords();
      showToast('Record added successfully!');
    } catch (error) {
      console.error('Error adding record:', error);
      showToast('Failed to add record.', true);
    }
  };

  const handleAddNumberLink = async (newLink) => {
    try {
      await axios.post(NUMBER_LINKS_API_URL, newLink);
      fetchRecords();
      showToast('Number Link added successfully!');
    } catch (error) {
      console.error('Error adding number link:', error);
      showToast('Failed to add number link.', true);
    }
  };

  const showToast = (msg, isError = false) => {
    setToast(msg);
    setToastError(isError);
    setTimeout(() => setToast(null), 3500);
  };

  const handleRecordChanged = (msg, isError = false) => {
    fetchRecords();
    showToast(msg, isError);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setToken(null);
    setRole('employee');
  };

  if (!token) {
    return (
      <div className="app-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Login setToken={(t) => {
          setToken(t);
          setRole(localStorage.getItem('role') || 'employee');
        }} />
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* Sidebar Navigation */}
      <aside className="sidebar">
        <div className="brand">
          <h1>AVANTICA PUC</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={toggleTheme} className="theme-btn" aria-label="Toggle Theme">
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <button onClick={handleLogout} className="theme-btn hide-on-desktop" style={{ color: '#ef4444' }}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
        <nav className="nav-links">
          <button 
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <PieChart size={20} /> Dashboard
          </button>
          <button 
            className={`nav-btn ${activeTab === 'registry' ? 'active' : ''}`}
            onClick={() => setActiveTab('registry')}
          >
            <FilePlus size={20} /> PUC Entry
          </button>
          <button 
            className={`nav-btn ${activeTab === 'number-link' ? 'active' : ''}`}
            onClick={() => setActiveTab('number-link')}
          >
            <LinkIcon size={20} /> Link / Reprint
          </button>
          <button 
            className={`nav-btn ${activeTab === 'daily' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily')}
          >
            <LayoutDashboard size={20} /> Daily Book
          </button>
          {role === 'admin' && (
            <button 
              className={`nav-btn ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('accounts')}
            >
              <BookOpen size={20} /> Account Book
            </button>
          )}
          <button 
            className={`nav-btn ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <FileText size={20} /> Custom Reports
          </button>
          <button 
            className={`nav-btn ${activeTab === 'renewals' ? 'active' : ''}`}
            onClick={() => setActiveTab('renewals')}
          >
            <BellRing size={20} /> Expiries
          </button>
          <button 
            className="nav-btn hide-on-mobile"
            style={{ marginTop: 'auto' }}
          >
            <Settings size={20} /> Settings
          </button>
          <button 
            className="nav-btn hide-on-mobile"
            onClick={handleLogout}
            style={{ color: '#ef4444' }}
          >
            <LogOut size={20} /> Logout
          </button>
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="main-wrapper">
        <div className="page-header">
          <h2>
            {activeTab === 'dashboard' && 'Analytics Dashboard'}
            {activeTab === 'registry' && 'Register New Vehicle (PUC)'}
            {activeTab === 'number-link' && 'Register Number Link / PUC Reprint'}
            {activeTab === 'daily' && 'Daily Collection'}
            {activeTab === 'accounts' && 'Account Ledger Book'}
            {activeTab === 'reports' && 'Custom Reports'}
            {activeTab === 'renewals' && 'Upcoming Renewals'}
          </h2>
          <p>
            {activeTab === 'dashboard' && 'Visualize your business metrics and collection trends.'}
            {activeTab === 'registry' && 'Enter the details below to generate a new PUC record.'}
            {activeTab === 'number-link' && 'Enter the details below to log a new RTO number link or PUC reprint.'}
            {activeTab === 'daily' && 'View your total metrics and transaction details for today.'}
            {activeTab === 'accounts' && 'Track daily revenues, enter expenses, and calculate net profits.'}
            {activeTab === 'reports' && 'Filter and view historical records between any date range.'}
            {activeTab === 'renewals' && 'View PUCs approaching their expiration dates.'}
          </p>
        </div>

        <div className="page-content">
          {activeTab === 'dashboard' && (
            <Dashboard records={records} numberLinks={numberLinks} />
          )}
          {activeTab === 'registry' && (
            <PUCForm onSubmit={handleAddRecord} role={role} />
          )}
          {activeTab === 'number-link' && (
            <NumberLinkForm onSubmit={handleAddNumberLink} role={role} />
          )}
          {activeTab === 'daily' && (
            <DailyBook records={records} numberLinks={numberLinks} token={token} role={role} onRecordChanged={handleRecordChanged} />
          )}
          {activeTab === 'accounts' && role === 'admin' && (
            <AccountBook records={records} numberLinks={numberLinks} expenses={expenses} token={token} onRecordChanged={handleRecordChanged} />
          )}

        {activeTab === 'reports' && (
          <Reports records={records} numberLinks={numberLinks} />
        )}
        {activeTab === 'renewals' && (
          <Renewals token={token} />
        )}
        </div>
      </main>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toastError ? 'toast-error' : 'toast-success'}`}>
          {toastError ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
          <span style={{ flex: 1 }}>{toast}</span>
          <button className="toast-close-btn" onClick={() => setToast(null)} aria-label="Close Notification">
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
